import type { ColumnDef } from '@tanstack/react-table';
import {
  createActionsColumn,
  createSerialColumn,
  MemberNameCell,
  StackedCellText,
} from '@/components/common/DataTable';
import { EmptyValue } from '@/components/common/EmptyValue';
import { RowActionGroup, RowViewLink } from '@/components/common/RowActions';
import { formatNumber } from '@/utils/number';
import type { ZonalStatementRow, ZonalStatementSearch } from '../types/zonalStatement.types';

export function getZonalStatementColumns(
  statementSearch: ZonalStatementSearch,
): ColumnDef<ZonalStatementRow, unknown>[] {
  return [
    createSerialColumn<ZonalStatementRow>(),
    {
      accessorKey: 'name',
      header: 'Full Name',
      cell: ({ row }) => <MemberNameCell member={row.original} />,
    },
    {
      id: 'department',
      header: 'Department / Designation',
      cell: ({ row }) => (
        <StackedCellText
          primary={row.original.departmentName ?? <EmptyValue />}
          secondary={row.original.designationName}
        />
      ),
    },
    {
      id: 'area',
      header: 'Division / District / Thana',
      cell: ({ row }) => (
        <span className="text-sm">
          {[row.original.divisionName, row.original.districtName, row.original.thanaName]
            .filter(Boolean)
            .join(', ') || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'dcrAmount',
      header: 'DCR (Tk)',
      cell: ({ row }) => (
        <span className="tabular-nums">{formatNumber(row.original.dcrAmount)}</span>
      ),
    },
    {
      accessorKey: 'derAmount',
      header: 'DER (Tk)',
      cell: ({ row }) => (
        <span className="tabular-nums">{formatNumber(row.original.derAmount)}</span>
      ),
    },
    {
      accessorKey: 'specimenQty',
      header: 'Specimen Qty',
      cell: ({ row }) => (
        <span className="tabular-nums">{formatNumber(row.original.specimenQty)}</span>
      ),
    },
    createActionsColumn<ZonalStatementRow>((member) => (
      <RowActionGroup>
        <RowViewLink
          to="/dashboards/zonal-statement/$userId/expense"
          params={{ userId: member.userId }}
          search={statementSearch}
          label="Expense"
          aria-label={`Expense statement of ${member.name}`}
        />
        <RowViewLink
          to="/dashboards/zonal-statement/$userId/specimen"
          params={{ userId: member.userId }}
          search={statementSearch}
          label="Specimen"
          aria-label={`Specimen statement of ${member.name}`}
        />
      </RowActionGroup>
    )),
  ];
}
